/** @jsxImportSource react */
import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api';
import { useTranslation } from '../i18n';
import { Order, OrderStatus } from '../types';
import { Search, Package, Clock, CheckCircle, Ship, DollarSign, X, AlertCircle, ExternalLink, Truck } from 'lucide-react';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { toast } from '../components/ui/Toast';

const getStatusIcon = (status: OrderStatus) => {
  switch (status) {
    case OrderStatus.Received: return <Clock className="h-5 w-5" />;
    case OrderStatus.Quoted: return <DollarSign className="h-5 w-5" />;
    case OrderStatus.Confirmed: return <CheckCircle className="h-5 w-5" />;
    case OrderStatus.Purchased: return <DollarSign className="h-5 w-5" />;
    case OrderStatus.Shipped: return <Ship className="h-5 w-5" />;
    case OrderStatus.Delivered: return <Package className="h-5 w-5" />;
    case OrderStatus.Cancelled: return <X className="h-5 w-5" />;
    default: return <Clock className="h-5 w-5" />;
  }
};

const OrderTimeline = ({ order }: { order: Order }) => {
  const { t, formatDate } = useTranslation();
  const statuses = Object.values(OrderStatus);
  
  const statusTimestamps = useMemo(() => {
    const map = new Map<OrderStatus, string>();
    if (order.statusHistory) {
      for (const history of order.statusHistory) {
        map.set(history.status, history.timestamp);
      }
    }
    return map;
  }, [order.statusHistory]);

  const getTrackingLink = (carrier?: string, trackingNumber?: string) => {
    if (!carrier || !trackingNumber) return '#';
    const lowerCaseCarrier = carrier.toLowerCase();
    
    if (lowerCaseCarrier.includes('ups')) {
      return `https://www.ups.com/track?tracknum=${trackingNumber}`;
    }
    if (lowerCaseCarrier.includes('dhl')) {
      return `https://www.dhl.com/en/express/tracking.html?AWB=${trackingNumber}`;
    }
    if (lowerCaseCarrier.includes('fedex')) {
      return `https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`;
    }
    // Default to a generic Google search
    return `https://www.google.com/search?q=${carrier}+tracking+${trackingNumber}`;
  };

  return (
    <div className="mt-8">
      <h3 className="text-lg font-bold text-secondary-900 mb-6">{t('orderProgress')}</h3>
      <div className="relative border-s-2 border-secondary-200 ms-3 md:ms-6 space-y-8">
        {statuses.map((status) => {
          const timestamp = statusTimestamps.get(status);
          const isCompleted = timestamp !== undefined;
          const isCurrent = order.status === status;
          
          // Skip irrelevant statuses for certain order types if needed
          if (order.orderType === 'Shipping' && [OrderStatus.Quoted, OrderStatus.Purchased].includes(status)) {
             return null;
          }

          return (
            <div key={status} className="relative ps-8 md:ps-10">
              <span
                className={`absolute -start-[9px] top-0 flex items-center justify-center w-5 h-5 rounded-full ring-4 ring-white transition-colors duration-300
                  ${isCompleted ? 'bg-primary-500' : 'bg-secondary-200'}
                `}
              >
                {isCompleted && <CheckCircle className="w-3 h-3 text-white" />}
              </span>
              
              <div className={`p-4 rounded-lg border transition-all duration-300 ${isCurrent ? 'bg-primary-50 border-primary-200 shadow-sm' : 'bg-white border-transparent'}`}>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <span className={`p-1.5 rounded-md ${isCompleted ? 'bg-primary-100 text-primary-600' : 'bg-secondary-100 text-secondary-500'}`}>
                            {getStatusIcon(status)}
                        </span>
                        <h4 className={`font-semibold ${isCompleted ? 'text-secondary-900' : 'text-secondary-500'}`}>
                            {t(status.toLowerCase())}
                        </h4>
                    </div>
                    {timestamp && (
                        <span className="text-xs font-medium text-secondary-500 bg-white px-2 py-1 rounded border border-secondary-100">
                             {formatDate(timestamp, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </span>
                    )}
                </div>
                
                {status === OrderStatus.Shipped && order.trackingNumber && isCompleted && (
                     <div className="mt-3 flex items-center gap-3 text-sm bg-white p-3 rounded border border-secondary-200">
                        <Truck className="h-4 w-4 text-primary-500" />
                        <div>
                            <span className="text-secondary-600">{t('trackingNumber')}: </span>
                            <a 
                                href={getTrackingLink(order.carrier, order.trackingNumber)} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="font-medium text-primary-600 hover:underline inline-flex items-center gap-1"
                            >
                                {order.trackingNumber}
                                <ExternalLink className="h-3 w-3" />
                            </a>
                             {order.carrier && <span className="text-secondary-500 ms-2">({order.carrier})</span>}
                        </div>
                     </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const TrackShipmentPage = () => {
  const { t } = useTranslation();
  const [orderId, setOrderId] = useState('');
  const [searchedId, setSearchedId] = useState('');
  
  const { data: order, isLoading, isError } = useQuery({
    queryKey: ['order', searchedId],
    queryFn: () => api.getOrderById(searchedId),
    enabled: !!searchedId,
    retry: false,
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!orderId.trim()) {
        toast.error(t('enterOrderId'));
        return;
    }
    setSearchedId(orderId.trim());
  };

  return (
    <div className="min-h-screen bg-secondary-50 p-4 md:p-8">
      <div className="max-w-3xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-secondary-900 mb-2">{t('trackShipment')}</h1>
            <p className="text-secondary-600">{t('trackShipmentDesc')}</p>
        </div>

        {/* Search Card */}
        <Card className="border-none shadow-lg">
            <CardContent className="p-6">
                <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-secondary-400" />
                        <Input 
                            value={orderId}
                            onChange={(e) => setOrderId(e.target.value)}
                            placeholder={t('enterOrderIdPlaceholder')} 
                            className="ps-10 h-12 text-lg"
                        />
                    </div>
                    <Button type="submit" size="lg" disabled={isLoading} isLoading={isLoading} className="h-12 px-8">
                        {t('track')}
                    </Button>
                </form>
            </CardContent>
        </Card>

        {/* Error State */}
        {isError && (
            <div className="bg-white border border-rose-100 rounded-xl p-8 text-center shadow-sm animate-fade-in">
                <div className="w-16 h-16 bg-rose-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertCircle className="h-8 w-8 text-rose-500" />
                </div>
                <h3 className="text-lg font-semibold text-secondary-900 mb-1">{t('orderNotFound')}</h3>
                <p className="text-secondary-600">{t('checkOrderId')}</p>
            </div>
        )}

        {/* Result State */}
        {order && (
            <Card className="border-none shadow-lg animate-fade-up overflow-hidden">
                <div className="bg-primary-600 p-6 text-white">
                    <div className="flex justify-between items-start">
                        <div>
                            <p className="text-primary-100 text-sm uppercase tracking-wider font-semibold">{t('orderId')}</p>
                            <h2 className="text-3xl font-bold mt-1">{order.orderId}</h2>
                            <p className="text-primary-100 mt-2 flex items-center gap-2">
                                <Package className="h-4 w-4" />
                                {order.productName}
                            </p>
                        </div>
                        <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg border border-white/20 text-center">
                            <p className="text-xs text-primary-100 uppercase tracking-wider mb-1">{t('currentStatus')}</p>
                            <p className="font-bold text-lg">{t(order.status.toLowerCase())}</p>
                        </div>
                    </div>
                </div>
                
                <CardContent className="p-6 md:p-8">
                    {/* Quick Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 pb-8 border-b border-secondary-100">
                        <div>
                            <p className="text-xs text-secondary-500 uppercase tracking-wider mb-1">{t('date')}</p>
                            <p className="font-semibold text-secondary-900">
                                {new Date(order.createdAt).toLocaleDateString()}
                            </p>
                        </div>
                        <div>
                            <p className="text-xs text-secondary-500 uppercase tracking-wider mb-1">{t('type')}</p>
                            <p className="font-semibold text-secondary-900">
                                {t(order.orderType.toLowerCase())}
                            </p>
                        </div>
                         <div>
                            <p className="text-xs text-secondary-500 uppercase tracking-wider mb-1">{t('shipping')}</p>
                            <p className="font-semibold text-secondary-900">
                                {order.shippingType ? t(order.shippingType.toLowerCase()) : '-'}
                            </p>
                        </div>
                        {order.weight && (
                            <div>
                                <p className="text-xs text-secondary-500 uppercase tracking-wider mb-1">{t('weight')}</p>
                                <p className="font-semibold text-secondary-900">
                                    {order.weight} kg
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Timeline */}
                    <OrderTimeline order={order} />

                </CardContent>
            </Card>
        )}
      </div>
    </div>
  );
};

export default TrackShipmentPage;